import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { AssessmentCriterion } from '@pirouette/shared';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import {
  useAssessmentTemplates,
  useCreateAssessmentTemplate,
  useDancerAssessments,
} from '@/data/assessments';

export const Route = createFileRoute('/admin/assessments/')({
  component: AssessmentsIndex,
});

const defaultCriteria: AssessmentCriterion[] = [
  { key: 'technique', label: 'Technique', scale: 'emerging_ready_strong' },
  { key: 'musicality', label: 'Musicality', scale: 'emerging_ready_strong' },
  { key: 'knowledge', label: 'Knowledge', scale: 'emerging_ready_strong' },
  { key: 'choreography', label: 'Choreography', scale: 'emerging_ready_strong' },
  { key: 'behavior', label: 'Behavior', scale: 'emerging_ready_strong' },
  { key: 'dress_code', label: 'Dress code', scale: 'emerging_ready_strong' },
  { key: 'corrections', label: 'Corrections', scale: 'emerging_ready_strong' },
];

function dancerName(dancer: {
  first_name: string;
  last_name: string;
  preferred_name: string | null;
}) {
  return `${dancer.preferred_name ?? dancer.first_name} ${dancer.last_name}`;
}

function formatSqlDate(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(year, month - 1, day));
}

function AssessmentsIndex() {
  const templates = useAssessmentTemplates();
  const assessments = useDancerAssessments();
  const createTemplate = useCreateAssessmentTemplate();
  const [name, setName] = useState('Davy Progress Check');

  const activeTemplates = useMemo(
    () => (templates.data ?? []).filter((template) => template.active),
    [templates.data],
  );

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await createTemplate.mutateAsync({
      name,
      criteria: defaultCriteria,
      active: true,
    });
    setName('Davy Progress Check');
  }

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Progress reports
        </p>
        <h1 className="mt-2">Assessments</h1>
      </header>

      <section aria-labelledby="new-template-heading" className="surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 id="new-template-heading" className="text-[var(--text-xl)]">
              Template library
            </h2>
            <p className="mt-1 max-w-2xl text-[var(--color-ink-muted)]">
              Create a reusable progress report with the studio's standard Davy criteria.
            </p>
          </div>
          <Badge tone="accent">{defaultCriteria.length} criteria</Badge>
        </div>

        <form
          onSubmit={(event) => void onSubmit(event)}
          className="mt-5 grid gap-4 md:grid-cols-[1fr_auto]"
        >
          <label className="grid gap-2">
            <span className="font-medium text-[var(--text-sm)]">Template name</span>
            <input
              className="h-11 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              maxLength={160}
            />
          </label>
          <button
            type="submit"
            className="self-end rounded-[var(--radius-md)] bg-[var(--color-accent)] px-5 py-3 font-medium text-[var(--color-ink-on-accent)]"
            disabled={createTemplate.isPending}
          >
            {createTemplate.isPending ? 'Creating...' : 'Create template'}
          </button>
        </form>

        <div className="mt-5 flex flex-wrap gap-2">
          {defaultCriteria.map((criterion) => (
            <Badge key={criterion.key}>{criterion.label}</Badge>
          ))}
        </div>
      </section>

      <section aria-labelledby="templates-heading" className="space-y-4">
        <h2 id="templates-heading" className="text-[var(--text-xl)]">
          Active templates
        </h2>

        {templates.isLoading && <p aria-live="polite">Loading...</p>}

        {activeTemplates.length === 0 && !templates.isLoading ? (
          <Empty
            title="No assessment templates"
            description="Create a template before faculty record progress for dancers."
          />
        ) : (
          <ul className="grid gap-3 md:grid-cols-2">
            {activeTemplates.map((template) => (
              <li key={template.id} className="surface-elevated p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <h3 className="font-[var(--font-display)] text-[var(--text-xl)]">
                    {template.name}
                  </h3>
                  <Badge tone="success">active</Badge>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {template.criteria.map((criterion) => (
                    <Badge key={criterion.key}>{criterion.label}</Badge>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="recent-assessments-heading" className="space-y-4">
        <h2 id="recent-assessments-heading" className="text-[var(--text-xl)]">
          Recent assessments
        </h2>

        {assessments.isLoading && <p aria-live="polite">Loading...</p>}

        {(assessments.data ?? []).length === 0 && !assessments.isLoading ? (
          <Empty
            title="No assessments recorded"
            description="Faculty progress entries will appear here once saved."
          />
        ) : (
          <ul className="grid gap-3">
            {(assessments.data ?? []).map((assessment) => (
              <li
                key={assessment.id}
                className="surface flex flex-wrap items-start justify-between gap-4 p-4"
              >
                <div>
                  <h3 className="font-medium">
                    {assessment.dancers ? dancerName(assessment.dancers) : 'Dancer'}
                  </h3>
                  <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                    {assessment.assessment_templates?.name ?? 'Assessment'} ·{' '}
                    {formatSqlDate(assessment.assessed_on)}
                    {assessment.classes?.name ? ` · ${assessment.classes.name}` : ''}
                  </p>
                  {assessment.summary && (
                    <p className="mt-2 max-w-3xl text-[var(--color-ink-muted)]">
                      {assessment.summary}
                    </p>
                  )}
                </div>
                <Badge tone={assessment.parent_visible ? 'success' : 'neutral'}>
                  {assessment.parent_visible ? 'parent visible' : 'internal'}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
